import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import { useQuery } from "@tanstack/react-query";
import { MentorCard } from "../components/MentorCard";
import { fetchMentoresWithDetails } from "../services/mentorService";
import { fetchBannersByPage } from "../services/bannersService";
import { PageBanner } from "../components/common/PageBanner";
import { Gamepad2, Search, SlidersHorizontal, Crown, Trophy, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { useFeaturedItemsForPage } from "@/hooks/useFeaturedItems";

const MentoresPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const { toast } = useToast();

  // Query para buscar banners da página de mentores
  const { data: banners } = useQuery({
    queryKey: ["banners", "mentores"],
    queryFn: () => fetchBannersByPage("mentores"),
  });

  // Query para buscar mentores em destaque
  const { data: featuredMentors } = useFeaturedItemsForPage('mentor');

  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ["mentores-with-details"],
    queryFn: async () => {
      console.log("Fetching mentors with details");
      try {
        const mentors = await fetchMentoresWithDetails();
        console.log(`Fetched ${mentors.length} mentors successfully`);

        // Log mentor data for debugging
        if (mentors.length > 0) {
          console.log("Sample mentor data:", mentors[0]);
          console.log("Sample mentor name:", mentors[0].name);
        } else {
          console.log("No mentors found, this could be a permission issue");
        }
        return mentors;
      } catch (err) {
        console.error("Error in fetchMentoresWithDetails query:", err);
        toast({
          title: "Erro ao carregar mentores",
          description: err instanceof Error ? err.message : "Erro desconhecido",
          variant: "destructive"
        });
        throw err;
      }
    }
  });

  useEffect(() => {
    console.log("MentoresPage rendered, auth status:", !!data);
    console.log("Mentors data:", data ? data.length : "no data");
    if (data?.length === 0) {
      console.log("WARNING: No mentors fetched - this may indicate an issue with RLS policies");
    } else if (data && data.length > 0) {
      // Log mentor names to check if they're correct
      console.log("Mentor names:", data.map(mentor => mentor.name));
    }
  }, [data]);

  // Get unique list of games from all mentors
  const allGames = data ? Array.from(new Map(data.flatMap(mentor => mentor.games).map(game => [game.id, game])).values()).sort((a, b) => a.name.localeCompare(b.name)) : [];

  // Filter mentors based on search term and selected game
  const filteredMentors = data ? data.filter(mentor => {
    const matchesSearch = searchTerm ? mentor.name.toLowerCase().includes(searchTerm.toLowerCase()) || mentor.bio && mentor.bio.toLowerCase().includes(searchTerm.toLowerCase()) : true;
    const matchesGame = selectedGame ? mentor.games.some(game => game.id === selectedGame) : true;
    return matchesSearch && matchesGame;
  }) : [];

  // Debug logs
  console.log('🔍 [MentoresPage] Featured mentors data:', featuredMentors);
  console.log('🔍 [MentoresPage] All mentors data:', data?.length);
  console.log('🔍 [MentoresPage] Filtered mentors:', filteredMentors?.length);

  // Separar mentores em destaque dos normais
  const featuredMentorIds = featuredMentors?.map(item => item.item_id) || [];
  const featuredMentorsList = filteredMentors.filter(mentor => featuredMentorIds.includes(mentor.profile_id));
  const regularMentorsList = filteredMentors.filter(mentor => !featuredMentorIds.includes(mentor.profile_id));
  
  // Debug logs para separação
  console.log('🎯 [MentoresPage] Featured mentor IDs:', featuredMentorIds);
  console.log('🎯 [MentoresPage] All mentor profile_ids:', filteredMentors.map(m => m.profile_id));
  console.log('🎯 [MentoresPage] Featured mentors list:', featuredMentorsList?.length);
  console.log('🎯 [MentoresPage] Regular mentors list:', regularMentorsList?.length);
  
  // Debug individual matches
  filteredMentors.forEach(mentor => {
    const isFeatured = featuredMentorIds.includes(mentor.profile_id);
    console.log(`🔍 Mentor ${mentor.name} (profile_id: ${mentor.profile_id}) - Featured: ${isFeatured}`);
  });

  // Ordenar mentores em destaque por posição
  const sortedFeaturedMentors = featuredMentorsList.sort((a, b) => {
    const positionA = featuredMentors?.find(item => item.item_id === a.profile_id)?.position || 999;
    const positionB = featuredMentors?.find(item => item.item_id === b.profile_id)?.position || 999;
    return positionA - positionB;
  });

  // Função para obter ícone de posição
  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1: return <Crown className="h-4 w-4 text-yellow-500" />;
      case 2: return <Trophy className="h-4 w-4 text-gray-400" />;
      case 3: return <Star className="h-4 w-4 text-amber-600" />;
      default: return null;
    }
  };

  // Função para obter cor da borda de destaque
  const getFeaturedBorderClass = (position: number) => {
    switch (position) {
      case 1: return 'border-yellow-500 shadow-yellow-500/20';
      case 2: return 'border-gray-400 shadow-gray-400/20';
      case 3: return 'border-amber-600 shadow-amber-600/20';
      default: return '';
    }
  };

  return (
    <div className="bg-[#0f141c] min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4">
        {/* Banner da página - sempre exibe um banner com gradiente */}
        <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-8">
          {banners && banners.length > 0 ? (
            <PageBanner banners={banners} />
          ) : (
            <div className="relative w-full overflow-hidden">
              {/* Gradiente consistente aplicado sempre */}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#18222F]/50 to-[#0f141c] z-10 pointer-events-none"></div>
              
              {/* Banner padrão quando não há banners customizados */}
              <div className="w-full h-80 bg-gradient-to-r from-[#1A2233] via-[#223342] to-[#2A3749] flex items-center justify-center">
                <div className="text-center text-white relative z-20">
                  <h2 className="text-4xl font-bold mb-4">Encontre Mentores Pro Players</h2>
                  <p className="text-xl text-gray-300">Melhore suas habilidades com os melhores jogadores</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-6 pt-6">
          {/* Sidebar with Filters */}
          <div className="w-full lg:w-64 lg:min-w-64">
            <div className="bg-[#181D24] rounded-lg p-4 border border-[#222A35] sticky top-24">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <SlidersHorizontal className="h-5 w-5 text-[#00B6F0]" />
                Filtros
              </h2>

              <div className="mb-6">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                  <Input placeholder="Buscar mentor..." className="pl-10 bg-[#1A2233] border-[#2D3748] text-white" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                </div>
              </div>

              <div className="mb-3">
                <h3 className="text-[#00B6F0] font-semibold text-sm mb-2 flex items-center gap-2">
                  <Gamepad2 className="h-4 w-4" /> 
                  Jogos
                </h3>
                <div className="space-y-2">
                  <Badge variant={!selectedGame ? "default" : "outline"} className={`cursor-pointer ${!selectedGame ? "bg-[#00B6F0] text-black" : "bg-[#1A2233] text-gray-300 border-[#2D3748]"}`} onClick={() => setSelectedGame(null)}>
                    Todos
                  </Badge>
                  
                  {allGames.map(game => <Badge key={game.id} variant={selectedGame === game.id ? "default" : "outline"} className={`cursor-pointer ml-1 ${selectedGame === game.id ? "bg-[#00B6F0] text-black" : "bg-[#1A2233] text-gray-300 border-[#2D3748]"}`} onClick={() => setSelectedGame(game.id)}>
                      {game.name}
                    </Badge>)}
                </div>
              </div>
              
              {selectedGame && <Button variant="link" className="text-[#00B6F0] p-0 text-sm" onClick={() => setSelectedGame(null)}>
                  Limpar filtros
                </Button>}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <h1 className="text-3xl font-extrabold text-[#00B6F0] mb-6">
              Mentores
            </h1>

            {/* Loading State */}
            {isLoading && <div className="text-white text-center py-12">
                Carregando mentores...
              </div>}

            {/* Error State */}
            {error && <div className="text-red-500 text-center py-12">
                Erro ao carregar mentores: {error instanceof Error ? error.message : "Erro desconhecido"}
                <Button variant="outline" onClick={() => refetch()} className="mt-4">
                  Tentar novamente
                </Button>
              </div>}

            {/* No Results */}
            {filteredMentors && filteredMentors.length === 0 && !isLoading && <div className="text-white text-center py-12">
                {searchTerm || selectedGame ? "Nenhum mentor encontrado com os filtros selecionados." : "Nenhum mentor cadastrado ainda."}
                <Button variant="outline" onClick={() => refetch()} className="mt-4">
                  Atualizar
                </Button>
              </div>}

            {/* Mentores em Destaque */}
            {sortedFeaturedMentors.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <Star className="h-6 w-6 text-[#00B6F0]" />
                  Mentores em Destaque
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sortedFeaturedMentors.map(mentor => {
                    const featuredItem = featuredMentors?.find(item => item.item_id === mentor.profile_id);
                    const position = featuredItem?.position || 0;
                    console.log(`Rendering FEATURED MentorCard for ${mentor.id} with name: "${mentor.name}" at position ${position}`);
                    return (
                      <div key={mentor.id} className="relative">
                        {/* Badge de posição */}
                        <div className="absolute -top-2 -right-2 z-30 bg-gradient-to-r from-[#00B6F0] to-[#0099CC] rounded-full p-2 shadow-lg">
                          {getPositionIcon(position)}
                        </div>
                        <div className={`border-2 ${getFeaturedBorderClass(position)} rounded-xl shadow-lg`}>
                          <MentorCard 
                            key={mentor.id} 
                            id={mentor.id} 
                            slug={mentor.slug} 
                            imageUrl={mentor.image_url} 
                            coverUrl={mentor.cover_url} 
                            name={mentor.name} 
                            bio={mentor.bio} 
                            averageRating={mentor.average_rating} 
                            minimumPrice={mentor.minimum_price} 
                            games={mentor.games}
                            isFeatured={true}
                            position={position}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Outros Mentores */}
            {regularMentorsList.length > 0 && (
              <div className="mb-8">
                {sortedFeaturedMentors.length > 0 && (
                  <h2 className="text-2xl font-bold text-white mb-4">
                    Outros Mentores
                  </h2>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {regularMentorsList.map(mentor => {
                    console.log(`Rendering regular MentorCard for ${mentor.id} with name: "${mentor.name}"`);
                    return (
                      <MentorCard 
                        key={mentor.id} 
                        id={mentor.id} 
                        slug={mentor.slug} 
                        imageUrl={mentor.image_url} 
                        coverUrl={mentor.cover_url} 
                        name={mentor.name} 
                        bio={mentor.bio} 
                        averageRating={mentor.average_rating} 
                        minimumPrice={mentor.minimum_price} 
                        games={mentor.games}
                        isFeatured={false}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentoresPage;